import React, { useCallback } from 'react'
import { Currency, Pair, TokenAmount } from 'vvs-sdk'
import { Button, Text, Flex, InjectedModalProps } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import TransactionConfirmationModal, {
  ConfirmationModalContent,
  TransactionErrorContent,
} from 'components/TransactionConfirmationModal'
import styled from 'styled-components'
import { AutoRow, RowBetween, RowFixed } from 'components/Layout/Row'
import { AutoColumn } from 'components/Layout/Column'
import { CurrencyLogo, DoubleCurrencyLogo } from 'components/Logo'
import { ContextData } from 'contexts/Localization/types'
import Dots from 'components/Loader/Dots'
import BigNumber from 'bignumber.js'
import { formatNumber, getBalanceAmount } from 'utils/formatBalance'
import QuestionHelper from 'components/QuestionHelper'
import { DeserializedRemovalOrder } from 'state/types'
import { unwrappedToken } from 'utils/wrappedCurrency'
import { FixedHeightRowEnd } from 'views/Pool/components/styles'

const FixedHeightRow = styled(RowBetween)`
  height: 24px;
`

function DuplicatedOrderModalFooter({
  onConfirm,
  onDismiss,
  pendingVvs,
  t,
}: {
  onConfirm: () => void
  onDismiss: () => void
  pendingVvs: BigNumber
  t: (key: string, data?: ContextData) => string
}) {
  return (
    <AutoRow>
      <AutoColumn gap="4px" style={{ paddingLeft: '14px', paddingRight: '14px', marginBottom: '.5rem' }}>
        <Flex flexDirection="column">
          <RowFixed>
            <Text ml="4px" textTransform="uppercase" fontSize="13px">
              {t('VVS Earned')}
            </Text>
            <QuestionHelper text={t('VVS earned in corresponding farm')} ml={1} />
          </RowFixed>
          <RowFixed>
            <Text ml="6px">
              {getBalanceAmount(pendingVvs).toNumber().toLocaleString('en-US', { maximumFractionDigits: 3 })}
            </Text>
          </RowFixed>
        </Flex>
      </AutoColumn>
      <Button variant="primary" onClick={onConfirm} scale="lg" mt="12px" id="confirm-remove-or-send" width="100%">
        {t('Cancel Order')}
      </Button>
      <Button variant="secondary" onClick={onDismiss} scale="lg" mt="12px" id="confirm-remove-or-send" width="100%">
        {t('Keep Order')}
      </Button>
    </AutoRow>
  )
}

const getSign = (amt, percent: number) => (amt === 0 || percent === 0 ? '±' : percent > 0 ? '+' : '-')

function DuplicatedOrderModalHeader({
  currencyA,
  currencyB,
  amount0,
  amount1,
  pooledToken0,
  pooledToken1,
  lpAmount,
  t,
}: {
  currencyA: Currency
  currencyB: Currency
  amount0: BigNumber
  amount1: BigNumber
  pooledToken0: TokenAmount
  pooledToken1: TokenAmount
  lpAmount: BigNumber
  t: (key: string, data?: ContextData) => string
}) {
  if (!currencyA || !currencyB) {
    return <Text>Loading...</Text>
  }
  const [token0Denom, token1Denom] = [
    parseFloat(pooledToken0?.toSignificant(6)),
    parseFloat(pooledToken1?.toSignificant(6)),
  ]
  const [parsedAmount0, parsedAmount1] = [
    getBalanceAmount(amount0, currencyA.decimals).toNumber(),
    getBalanceAmount(amount1, currencyB.decimals).toNumber(),
  ]
  return (
    <AutoColumn gap="sm">
      <Text textAlign="center" color="textSubtle" px={1}>
        {t(
          'You currently have an existing open removal order for this liquidity pool. To place a new order you must first cancel the existing order.',
        )}
      </Text>
      <AutoColumn gap="8px" style={{ padding: '14px' }}>
        <Flex flexDirection="column" mt=".7rem" mb=".5rem">
          <Flex alignItems="center">
            <DoubleCurrencyLogo currency0={currencyA} currency1={currencyB} size={20} />
            <Text bold ml="8px">
              {!currencyA || !currencyB ? <Dots>{t('Loading')}</Dots> : `${currencyA.symbol}/${currencyB.symbol}`}
            </Text>
          </Flex>
          <Text fontSize="14px" color="textSubtle">
            {formatNumber(getBalanceAmount(lpAmount).toNumber(), 0, 18)}
          </Text>
        </Flex>
        <FixedHeightRow>
          <Text mb={1} textTransform="uppercase">
            {t('You will receive')}
          </Text>
        </FixedHeightRow>
        <FixedHeightRow>
          <RowFixed>
            <CurrencyLogo size="20px" currency={currencyA} />
            <Text color="textSubtle" ml="4px">
              {currencyA.symbol}
            </Text>
          </RowFixed>
          {amount0 ? (
            <RowFixed>
              <Text ml="6px">
                {formatNumber(getBalanceAmount(amount0, currencyA.decimals).toNumber(), 0, currencyA.decimals)}
              </Text>
            </RowFixed>
          ) : (
            '-'
          )}
        </FixedHeightRow>
        <FixedHeightRowEnd mt={-1}>
          <Text color="textSubtle">
            {t('(Target: %percent%%)', {
              percent: !amount0
                ? '-'
                : `${getSign(parsedAmount0, ((parsedAmount0 - token0Denom) / token0Denom) * 100)}${
                    parsedAmount0 === 0 ? '0' : Math.abs(((parsedAmount0 - token0Denom) / token0Denom) * 100).toFixed(2)
                  }`,
            })}
          </Text>
        </FixedHeightRowEnd>

        <FixedHeightRow>
          <RowFixed>
            <CurrencyLogo size="20px" currency={currencyB} />
            <Text color="textSubtle" ml="4px">
              {currencyB.symbol}
            </Text>
          </RowFixed>
          {amount1 ? (
            <RowFixed>
              <Text ml="6px">
                {formatNumber(getBalanceAmount(amount1, currencyB.decimals).toNumber(), 0, currencyB.decimals)}
              </Text>
            </RowFixed>
          ) : (
            '-'
          )}
        </FixedHeightRow>
        <FixedHeightRowEnd mt={-1} mb={3}>
          <Text color="textSubtle">
            {t('(Target: %percent%%)', {
              percent: !amount1
                ? '-'
                : `${getSign(parsedAmount1, ((parsedAmount1 - token1Denom) / token1Denom) * 100)}${
                    parsedAmount1 === 0 ? '0' : Math.abs(((parsedAmount1 - token1Denom) / token1Denom) * 100).toFixed(2)
                  }`,
            })}
          </Text>
        </FixedHeightRowEnd>
      </AutoColumn>
    </AutoColumn>
  )
}

interface DuplicatedOrderWarningModalProps {
  attemptingTxn: boolean
  txHash?: string
  onConfirm: () => void
  errorMessage?: string
  customOnDismiss?: () => void
  pooledToken0: TokenAmount
  pooledToken1: TokenAmount
  pair: Pair
  order: DeserializedRemovalOrder
}

const DuplicatedOrderWarningModal: React.FC<InjectedModalProps & DuplicatedOrderWarningModalProps> = ({
  onConfirm,
  onDismiss,
  customOnDismiss,
  errorMessage,
  attemptingTxn,
  txHash,
  pooledToken0,
  pooledToken1,
  pair,
  order,
}) => {
  const { t } = useTranslation()

  const [currencyA, currencyB] = pair ? [unwrappedToken(pair.token0), unwrappedToken(pair?.token1)] : []

  const modalHeader = useCallback(() => {
    return (
      <DuplicatedOrderModalHeader
        currencyA={currencyA}
        currencyB={currencyB}
        amount0={order?.userData?.token0Amt}
        amount1={order?.userData?.token1Amt}
        pooledToken0={pooledToken0}
        pooledToken1={pooledToken1}
        lpAmount={order?.userData?.stakedBalance}
        t={t}
      />
    )
  }, [currencyA, currencyB, order, pooledToken0, pooledToken1, t])

  const modalBottom = useCallback(() => {
    return (
      <DuplicatedOrderModalFooter
        onConfirm={onConfirm}
        onDismiss={onDismiss}
        pendingVvs={order?.userData?.pendingVvs}
        t={t}
      />
    )
  }, [onConfirm, onDismiss, order, t])

  const confirmationContent = useCallback(
    () =>
      errorMessage ? (
        <TransactionErrorContent onDismiss={onDismiss} customOnDismiss={customOnDismiss} message={errorMessage} />
      ) : (
        <ConfirmationModalContent topContent={modalHeader} bottomContent={modalBottom} />
      ),
    [onDismiss, modalBottom, modalHeader, errorMessage, customOnDismiss],
  )

  return (
    <TransactionConfirmationModal
      title={t('Warning')}
      onDismiss={onDismiss}
      customOnDismiss={customOnDismiss}
      attemptingTxn={attemptingTxn}
      hash={txHash}
      content={confirmationContent}
      pendingText={t('Cancel %token0%/%token1% removal order', {
        token0: currencyA?.symbol ?? '',
        token1: currencyB?.symbol ?? '',
      })}
      callbackOnExit
    />
  )
}

export default DuplicatedOrderWarningModal
